/**
 * Estado da sessão da plataforma (#430): localStorage + sync entre separadores.
 * Campos ao lado do access token — nunca IP/UA/ator.
 */
import { getTokenPayload } from './auth.js'

const SESSION_KEY = 'sr_impersonation'
const EXPIRED_KEY = 'sr_impersonation_expired'

export type ImpersonationSession = {
  tenant_id: string
  tenant_slug: string
  tenant_name: string
  reason: string
  expires_at: number // unix seconds
}

export type ImpersonationExpiredNotice = {
  restaurant: string
  tenant_slug: string
  ended_at: number // unix seconds
}

const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of listeners) fn()
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e) => {
    if (e.key === SESSION_KEY || e.key === EXPIRED_KEY || e.key === null) notify()
  })
}

/** Assina mudanças da sessão (esta aba ou outra). */
export function onImpersonationChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getImpersonationSession(): ImpersonationSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ImpersonationSession
    if (
      !parsed ||
      typeof parsed.tenant_slug !== 'string' ||
      typeof parsed.expires_at !== 'number' ||
      typeof parsed.tenant_name !== 'string'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function setImpersonationSession(session: ImpersonationSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    /* storage indisponível */
  }
  notify()
}

export function clearImpersonationSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch {
    /* storage indisponível */
  }
  notify()
}

export function getExpiredNotice(): ImpersonationExpiredNotice | null {
  try {
    const raw = localStorage.getItem(EXPIRED_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ImpersonationExpiredNotice
  } catch {
    return null
  }
}

export function setExpiredNotice(notice: ImpersonationExpiredNotice): void {
  try {
    localStorage.setItem(EXPIRED_KEY, JSON.stringify(notice))
  } catch {
    /* storage indisponível */
  }
  notify()
}

export function clearExpiredNotice(): void {
  try {
    localStorage.removeItem(EXPIRED_KEY)
  } catch {
    /* storage indisponível */
  }
  notify()
}

/** Sessão local ainda dentro do TTL. */
export function isImpersonationActive(nowSec = Math.floor(Date.now() / 1000)): boolean {
  const s = getImpersonationSession()
  return !!s && s.expires_at > nowSec
}

/** True se o JWT atual tem claim do tenant (ou qualquer claim). */
export function jwtHasImpersonatingClaim(tenantId?: string): boolean {
  const payload = getTokenPayload() as {
    impersonating?: { tenant_id?: string }
  } | null
  const claim = payload?.impersonating?.tenant_id
  if (!claim) return false
  return tenantId ? claim === tenantId : true
}

/**
 * Marca expiração, limpa a sessão e devolve o aviso para a consola.
 * Idempotente se já não houver sessão.
 * Grava notice + remove sessão no mesmo passo e notifica uma vez — se o
 * aviso fosse escrito com a sessão ainda presente, o sync da faixa tratava
 * como «sessão ativa» e apagava o estado expired local.
 */
export function markImpersonationExpired(
  nowSec = Math.floor(Date.now() / 1000),
): ImpersonationExpiredNotice | null {
  const s = getImpersonationSession()
  if (!s) return getExpiredNotice()
  const notice: ImpersonationExpiredNotice = {
    restaurant: s.tenant_name,
    tenant_slug: s.tenant_slug,
    ended_at: Math.min(nowSec, s.expires_at),
  }
  try {
    localStorage.setItem(EXPIRED_KEY, JSON.stringify(notice))
    localStorage.removeItem(SESSION_KEY)
  } catch {
    /* storage indisponível */
  }
  notify()
  return notice
}

export function clearAllImpersonationState(): void {
  clearImpersonationSession()
  clearExpiredNotice()
}
